<template>
    <div class="estimated-waste">
        <!-- Loading State -->
        <div v-if="loading" class="estimated-waste__loading">
            <div class="spinner"></div>
        </div>

        <template v-else>
            <div class="estimated-waste__summary">
            <!-- Gauge Chart -->
            <themed-card class="estimated-waste__gauge-card">
                <div class="estimated-waste__gauge-content">
                    <div class="estimated-waste__gauge-header">
                        <h3 class="estimated-waste__gauge-title">Total Estimated Waste</h3>
                        <InfoIcon identifier="waste-chart-totalestimatedwaste" />
                    </div>
                    
                    <div class="estimated-waste__gauge-chart">
                        <theme-gauge-chart
                            :value="wastePercentage"
                            :min="0"
                            :max="100"
                            :green-zone="[0, 25]"
                            :yellow-zone="[25, 50]"
                            :red-zone="[50, 100]"
                            :show-labels="true"
                        />
                    </div>
                    
                    <div class="estimated-waste__gauge-footer">
                        <span class="estimated-waste__gauge-text">Currently</span>
                        <span :class="['estimated-waste__gauge-badge', wasteColorClass]">
                            {{ wastePercentage }}%
                        </span>
                        <span class="estimated-waste__gauge-text">Total Estimated Waste</span>
                        <InfoIcon identifier="waste-chart-%totalestimatedwaste" />
                    </div>
                </div>
            </themed-card>

            <!-- Average Utilization -->
            <themed-card class="estimated-waste__metric-card">
                <div class="estimated-waste__metric-content estimated-waste__metric-content--center">
                    <div class="estimated-waste__utilization-chart">
                        <theme-doughnut-chart
                            :data="utilizationChartData"
                            :colors="utilizationChartColors"
                            :show-legend="false"
                            cutout="70%"
                        />
                    </div>
                    <div class="estimated-waste__metric-value">
                        {{ averageUtilization }}%
                    </div>
                    <div class="estimated-waste__metric-label">Average Utilization</div>
                </div>
            </themed-card>

            <!-- Total Portfolio NPV -->
            <themed-card class="estimated-waste__metric-card">
                <div class="estimated-waste__metric-content estimated-waste__metric-content--center">
                    <p class="estimated-waste__metric-value estimated-waste__metric-value--success" data-precision="0" :data-originalvalue="totalNPV">
                        {{ formatShorthandCurrency(totalNPV) }}
                    </p>
                    <p class="estimated-waste__metric-label">Total Portfolio NPV</p>
                </div>
            </themed-card>

            <!-- Total Estimated Waste -->
            <themed-card class="estimated-waste__metric-card">
                <div class="estimated-waste__metric-content estimated-waste__metric-content--center">
                    <div class="estimated-waste__metric-value-with-icon">
                        <p class="estimated-waste__metric-value estimated-waste__metric-value--danger" data-precision="0" :data-originalvalue="Math.round(totalWaste)">
                            {{ formatShorthandCurrency(Math.round(totalWaste)) }}
                        </p>
                        <InfoIcon identifier="waste-info-totalestimatedwaste" />
                    </div>
                    <p class="estimated-waste__metric-label">Total Estimated Waste</p>
                </div>
            </themed-card>
        </div>

        <site-cards
            :site-data="topTenSites"
            :show-toolbar="true"
            :show-title="true"
            title="Top 10 Sites with Waste"
            :show-pagination="true"
            :show-new-site-button="false"
            :show-view-toggle="false"
            :show-archived-toggle="false"
            :default-view-mode="'grid'"
            :from-estimated-waste="true"
        />
        </template>
    </div>
</template>

<script>   
    import ThemedCard from '../ThemedCard.vue';
    import ThemeGaugeChart from '../charts/ThemeGaugeChart.vue';
    import ThemeDoughnutChart from '../charts/ThemeDoughnutChart.vue';
    import SiteCards from '../sites/SiteCards.vue';
    import { formatShorthandCurrency } from '../../../../utils/formatters';

    export default {
        components: {
            ThemedCard,
            ThemeGaugeChart,
            ThemeDoughnutChart,
            SiteCards
        },

        data() {
            return {
                loading: true,
                allSites: []
            }
        },

        mounted() {
            this.loadEstimatedWasteData();
        },

        computed: {
            sitesWithWaste() {
                // Sites are already filtered and sorted by API
                return this.allSites;
            },

            // site.npv is stored in cents; site.estimated_waste is already in dollars.
            // Both totals are expressed in dollars so they can be compared and displayed directly.
            totalNPV() {
                return this.sitesWithWaste.reduce((sum, site) => sum + (site.npv || 0), 0) / 100;
            },

            totalWaste() {
                return this.sitesWithWaste.reduce((sum, site) => sum + (site.estimated_waste || 0), 0);
            },

            wastePercentage() {
                if (this.totalNPV === 0) return 0;
                return Math.round((this.totalWaste / this.totalNPV) * 100);
            },

            wasteColorClass() {
                if (this.wastePercentage > 50) return 'estimated-waste__gauge-badge--high';
                if (this.wastePercentage >= 25) return 'estimated-waste__gauge-badge--medium';
                return 'estimated-waste__gauge-badge--low';
            },

            // Weighted by square footage, matching UtilizationCard and LeasesDueUtilizationCard.
            // An unweighted mean lets a small site count as much as a large one, which produced a
            // different headline utilization here than on the dashboard for the same portfolio.
            averageUtilization() {
                const totalSize = this.sitesWithWaste.reduce((sum, site) => sum + (site.size || 0), 0);
                if (totalSize === 0) return 0;

                const usedSquareFootage = this.sitesWithWaste.reduce(
                    (sum, site) => sum + ((site.size || 0) * ((site.utilization || 0) / 100)), 0
                );

                return Math.round((usedSquareFootage / totalSize) * 100);
            },

            utilizationChartData() {
                const utilized = this.averageUtilization;
                const unutilized = 100 - utilized;
                
                return [
                    { label: 'Utilized', value: utilized },
                    { label: 'Unutilized', value: unutilized }
                ];
            },

            utilizationChartColors() {
                // Use theme-aware colors - green for utilized, muted for unutilized
                const isDark = document.documentElement.classList.contains('dark');
                return [
                    '#22c55e', // Green for utilized (works in both themes)
                    isDark ? '#334155' : '#e5e7eb' // Dark gray for dark mode, light gray for light mode
                ];
            },

            topTenSites() {
                return this.sitesWithWaste.slice(0, 10);
            }
        },

        methods: {
            formatShorthandCurrency,

            async loadEstimatedWasteData() {
                this.loading = true;
                
                try {
                    const response = await window.axios.get('/api/sites', {
                        params: {
                            estimated_waste: 'true',
                            order_by: 'estimated_waste',
                            order_direction: 'desc'
                        }
                    });
                    this.allSites = response.data.data || response.data;
                } catch (error) {
                    console.error('Failed to load sites data:', error);
                    if (this.$toast) {
                        this.$toast.error('Failed to load sites data');
                    }
                } finally {
                    this.loading = false;
                }
            }
        }
    }
</script>
